import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox } from "@mui/material";
import { FunctionComponent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { faEnvelope, faEye, faEyeSlash, faLock } from "@fortawesome/pro-regular-svg-icons";

import { NavLink, useNavigate } from "react-router-dom";

import { Icon } from "@/components";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { ROUTE_CONFIG } from "@/routes/config";

import FormContainer from "../../components/FormContainer";
import { useLoginMutation } from "../../redux/auth.api";
import { LoginSchema, LoginType } from "./schema/login";

const { JOIN_SCENARIO } = ROUTE_CONFIG;

export const Login: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`login.${key}`);
	const [passwordFieldType, setPasswordFieldType] = useState("password");
	const navigate = useNavigate();

	const [login, { isLoading, error }] = useLoginMutation();

	const initialValues: LoginType = {
		email: "",
		password: "",
		isRememberMe: true,
	};

	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors },
	} = useForm<LoginType>({
		defaultValues: initialValues,
		resolver: zodResolver(LoginSchema),
	});

	const onSubmit = async (values: LoginType) => {
		try {
			await login(values).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const isRememberMe = watch("isRememberMe");

	return (
		<div className="w-full h-full flex items-center justify-center relative">
			<Button
				className="!h-[44px] absolute top-4 right-4 !min-w-[150px] !max-w-[150px]"
				color={ButtonColor.ACTION_SECONDARY}
				size={ButtonSize.ML}
				title={t("courses.progress.join-course")}
				type="button"
				onClick={() => navigate(JOIN_SCENARIO)}
			/>
			<FormContainer
				description={ts("description")}
				formError={error}
				formFooter={
					<div className="w-full flex justify-between items-center">
						<div className="flex items-center gap-2">
							<Checkbox checked={isRememberMe} {...register("isRememberMe")} sx={{ p: "0px" }} />
							<p className="text-[14px] text-primary-blue text-nowrap">{ts("remember-me")}</p>
						</div>
						<NavLink className="bb-link text-[14px] text-nowrap" to={"/forgot-password"}>
							{ts("forgotPasswordCta")}
						</NavLink>
					</div>
				}
				handleSubmit={handleSubmit(onSubmit)}
				isLoading={isLoading}
				submitButtonText={ts("loginCta")}
				title={ts("title")}
			>
				<Controller
					control={control}
					name="email"
					render={({ field }) => (
						<FormTextField
							{...field}
							autoComplete="email"
							errorMessage={errors?.email?.message}
							formRegister={register("email")}
							label={t("basics.email")}
							placeHolder={t("basics.enterSomething", { something: t("basics.email") })}
							startAdornment={
								<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faEnvelope} />
							}
							type="email"
						/>
					)}
				/>
				<Controller
					control={control}
					name="password"
					render={({ field }) => (
						<FormTextField
							{...field}
							autoComplete="password"
							endAdornment={
								<button
									aria-label={passwordFieldType === "password" ? "Show password" : "Hide password"}
									type="button"
									onClick={() =>
										setPasswordFieldType(passwordFieldType === "password" ? "text" : "password")
									}
								>
									<Icon
										className="w-4 h-4 text-primary-gray-lighter"
										icon={passwordFieldType === "password" ? faEye : faEyeSlash}
									/>
								</button>
							}
							errorMessage={errors?.password?.message}
							label={t("basics.password")}
							placeHolder={t("basics.enterSomething", { something: t("basics.password") })}
							startAdornment={
								<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faLock} />
							}
							type={passwordFieldType}
						/>
					)}
				/>
			</FormContainer>
		</div>
	);
};
